import { Add as AddIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import type React from "react";
import { PageHeader } from "../../components/page-header";
import { SearchBar } from "../../components/search-bar";
import { TagChip } from "../../components/tag-chip";
import { SUGGESTED_TAGS } from "../../constants/tags";
import { useApiQuery } from "../../hooks/use-api-query";
import { useAvailableTags } from "../../hooks/use-tags";
import type { ApiConfig } from "../../types";
import type { FilterType } from "./types";

interface TestCasesHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filterType: FilterType;
  onFilterChange: (filter: FilterType) => void;
  isLoading: boolean;
  onCreateTest: () => void;
  showRejected: boolean;
  onShowRejectedChange: (show: boolean) => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export const TestCasesHeader: React.FC<TestCasesHeaderProps> = ({
  searchQuery,
  onSearchChange,
  filterType,
  onFilterChange,
  isLoading,
  onCreateTest,
  showRejected,
  onShowRejectedChange,
  selectedTags,
  onTagsChange,
}) => {
  const { data: availableTagsData } = useAvailableTags();
  const availableTags = availableTagsData?.tags || [];
  const { data: apiConfig } = useApiQuery<ApiConfig>("/config");
  const appConfigs = apiConfig?.configs || [];

  // Combine available tags with suggested tags, removing duplicates
  const allTagOptions = [...new Set([...availableTags, ...SUGGESTED_TAGS])];

  // Get display label for the current filter
  const getFilterLabel = (value: FilterType): string => {
    if (value === "all") return "All";
    if (value === "production") return "Production";
    if (value === "local") return "Local";
    const config = appConfigs.find((c) => c.id === value);
    return config?.name || value;
  };
  return (
    <PageHeader
      title="Test Cases"
      subtitle="All your test cases, automated or not, in a single place."
      actions={
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onCreateTest}
          sx={{ textTransform: "none" }}
        >
          New Test
        </Button>
      }
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1 }}>
          <SearchBar
            size="small"
            value={searchQuery}
            onChange={onSearchChange}
            loading={isLoading}
            label="Search..."
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                height: 36,
                fontSize: "0.8rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.8rem",
                color: "grey.500",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.400",
              },
            }}
          />
        </Box>
        <Box sx={{ minWidth: 180 }}>
          <Autocomplete
            multiple
            size="small"
            options={allTagOptions}
            value={selectedTags}
            onChange={(_, value) => onTagsChange(value)}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedTags.length === 0 ? "Filter by tags..." : ""}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.8rem",
                    minHeight: 36,
                    py: 0,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.400",
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <TagChip {...getTagProps({ index })} key={option} tag={option} size="small" />
              ))
            }
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box key={key} component="li" {...otherProps} sx={{ fontSize: "0.8rem", py: 0.5 }}>
                  <TagChip tag={option} size="small" variant="outlined" />
                </Box>
              );
            }}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filterType}
            onChange={(e) => onFilterChange(e.target.value)}
            displayEmpty
            renderValue={(value) => getFilterLabel(value as FilterType)}
            sx={{
              fontSize: "0.8rem",
              height: 36,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.400",
              },
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="production">Production (default)</MenuItem>
            <MenuItem value="local">Local</MenuItem>
            {appConfigs.length > 0 && <Divider />}
            {appConfigs.length > 0 && (
              <ListSubheader sx={{ fontSize: "0.75rem", lineHeight: "32px" }}>
                App Configs
              </ListSubheader>
            )}
            {appConfigs.map((config) => (
              <MenuItem key={config.id} value={config.id} sx={{ fontSize: "0.8rem" }}>
                {config.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={showRejected}
              onChange={(e) => onShowRejectedChange(e.target.checked)}
              size="small"
              sx={{
                color: "error.dark",
                "&.Mui-checked": {
                  color: "error.dark",
                },
              }}
            />
          }
          label="Show rejected"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "0.75rem",
              color: "text.secondary",
            },
          }}
        />
      </Stack>
    </PageHeader>
  );
};
